import type {
  ColorKey,
  MarginProperties,
  PaddingProperties,
} from '@iheartjane/reefer';
import { Accordion, Box, Flex, Link, Typography } from '@iheartjane/reefer';
import type { ProductKind } from '@iheartjane/types';
import type { PropsWithChildren } from 'react';

const prop65WarningsFlower = [
  "Smoking cannabis increases your cancer risk and during pregnancy exposes your child to delta-9-THC and other chemicals that can affect your child's birthweight, behavior, and learning ability.",
  "Consuming products during pregnancy exposes your child to delta-9-THC, which can affect your child's behavior and learning ability.",
  "Using transdermal products during pregnancy exposes your child to delta-9-THC, which can affect your child's behavior and learning ability.",
  'A spent cannabis cartridge shall be properly disposed of as hazardous waste at a household hazardous waste facility or other approved facility. An empty integrated cannabis vaporizer shall be properly disposed of as hazardous waste at a household hazardous waste collection facility or other approved facility.',
];

const prop65WarningsVape = [
  "Vaping or dabbing this product during pregnancy exposes your child to delta-9-THC, which can affect your child's behavior and learning ability.",
  "Consuming products during pregnancy exposes your child to delta-9-THC, which can affect your child's behavior and learning ability.",
  "Using transdermal products during pregnancy exposes your child to delta-9-THC, which can affect your child's behavior and learning ability.",
  'A spent cannabis cartridge shall be properly disposed of as hazardous waste at a household hazardous waste facility or other approved facility. An empty integrated cannabis vaporizer shall be properly disposed of as hazardous waste at a household hazardous waste collection facility or other approved facility.',
];

const kentuckyWarnings = [
  'Medicinal cannabis is for use by cardholders only.',
  'Keep out of reach of children.',
  'Flower is not intended for consumption by smoking.',
];

interface WarningProps extends PropsWithChildren {
  color?: ColorKey;
}

function Warning({ color, children }: WarningProps) {
  return (
    <Typography color={color}>
      <Typography variant="small-body-semibold" as="span" color={color}>
        WARNING:&nbsp;
      </Typography>
      {children}
    </Typography>
  );
}

export interface Prop65WarningsProps {
  color?: ColorKey;
  productKind?: ProductKind;
}

export function Prop65Warnings({
  color = 'text-main',
  productKind,
}: Prop65WarningsProps) {
  const warnings =
    productKind === 'vape' ? prop65WarningsVape : prop65WarningsFlower;

  return (
    <div data-testid="prop-65-warning">
      {warnings.map((warning) => (
        <Warning color={color} key={warning}>
          {warning}
        </Warning>
      ))}

      <Typography color={color}>
        For more information go to&nbsp;
        <Link href="https://www.P65Warnings.ca.gov/cannabis" color="inherit">
          www.P65Warnings.ca.gov/cannabis
        </Link>
      </Typography>
    </div>
  );
}

function KentuckyWarnings({ productKind }: { productKind?: ProductKind }) {
  const warningsToRender =
    productKind === 'flower' ? kentuckyWarnings : kentuckyWarnings.slice(0, 2);
  return (
    <div data-testid="kentucky-warning">
      {warningsToRender.map((warning) => (
        <Warning key={warning}>{warning}</Warning>
      ))}
    </div>
  );
}

export interface StateWarningsProps
  extends PaddingProperties, MarginProperties {
  expandedByDefault?: boolean;
  productKind?: ProductKind;
  state: string;
}

export function StateWarnings({
  expandedByDefault = false,
  productKind,
  state,
  ...spacingProps
}: StateWarningsProps) {
  const isCalifornia = state === 'California';
  const title = isCalifornia
    ? 'Proposition 65 Warning for California Consumers'
    : 'Warning for Kentucky Consumers';

  return (
    <Box data-testid="state-warning" {...spacingProps}>
      <Flex alignItems="center" gap={8} mb={8}>
        {isCalifornia && expandedByDefault && <span>⚠️</span>}
        <Typography variant="body-semibold">Warning</Typography>
      </Flex>
      <Accordion.Item id="iWarning" defaultExpanded={expandedByDefault}>
        <Accordion.Heading>
          <Flex my={0} pr={8} alignItems="center">
            <Typography as="span" mr={8}>
              {title}
            </Typography>
            <Accordion.Indicator iconSize="sm" />
          </Flex>
        </Accordion.Heading>
        <Accordion.Content>
          {isCalifornia ? (
            <Prop65Warnings productKind={productKind} />
          ) : (
            <KentuckyWarnings productKind={productKind} />
          )}
        </Accordion.Content>
      </Accordion.Item>
    </Box>
  );
}
